import LatLonSpherical from 'geodesy/latlon-spherical';
import {
    DataWithLocation,
    DataWithLocationArray,
} from 'app/shared/collections/time/ims_time_collection.factory';

/**
 * An arbitrary small factor so we don't have 'hard' edges on the edge of our bitmap
 */
const GRID_OVERLAP_FACTOR = 0.05;

/*
 * A grid square on the globe. It is used to spatially close data points
 * together but has no information about any further analysis for those data points.
 * e.g. aggregations
 */
export interface GridSquare {
    north: number; // value in degrees
    south: number; // value in degrees
    east: number; // value in degrees
    west: number; // value in degrees
    datums: DataWithLocationArray; // Stored datums in this grid square
}

/**
 * Breaks the entire world up into a number of grid squares.
 * Grid squares are lazily created as we assign each data point to a grid square.
 *
 * @param {DataWithLocationArray} datums
 * @param {number} gridSizeInMetres
 * @returns {GridSquare[]}
 */
export function dataByLocationToGrid(
    datums: DataWithLocationArray,
    gridSizeInMetres: number = 25,
): GridSquare[] {
    const centre = new LatLonSpherical(0, 0);
    const gridSquaresDict = {} as {
        [key: string]: GridSquare;
    };

    let north, south, east, west;

    datums.forEach((d: DataWithLocation) => {
        const latitude = d.lat;
        const longitude = d.lon;
        // Workout vectors based on the size of the world.
        const lonDirection = longitude > 0 ? 1 : -1;
        const latDirection = latitude > 0 ? -1 : 1;

        const xDistance = new LatLonSpherical(0, longitude).distanceTo(centre) * lonDirection;
        const yDistance = new LatLonSpherical(latitude, 0).distanceTo(centre) * latDirection;

        // Divide up the world into squares by size.
        const xSquare = Math.floor(xDistance / gridSizeInMetres);
        const ySquare = Math.floor(yDistance / gridSizeInMetres);
        // Our lookup, hah.
        const dictString = xSquare + '-' + ySquare;

        let gridSquare = gridSquaresDict[dictString];
        if (!gridSquare) {
            const adjustSize = Math.floor(gridSizeInMetres * GRID_OVERLAP_FACTOR);
            // Note the bearing 180, for north / south and
            // bearing 90 for east / west.
            // This makes the southerly direction the 'positive' for N/S
            // And the easterly direction the 'positive' for W/E

            // Expand the grid square in all 4 directions by the adjust size
            // Minus the adjust size from the edges that are on the 'minus' side.
            north = centre.destinationPoint(ySquare * gridSizeInMetres - adjustSize, -180);
            west = centre.destinationPoint(xSquare * gridSizeInMetres - adjustSize, 90);
            // Add the adjust size to the edges that are on the 'positive' side.
            south = centre.destinationPoint((ySquare + 1) * gridSizeInMetres + adjustSize, 180);
            east = centre.destinationPoint((xSquare + 1) * gridSizeInMetres + adjustSize, 90);

            gridSquare = {
                north: north.lat,
                south: south.lat,
                east: east.lon,
                west: west.lon,
                datums: [],
            };
            gridSquaresDict[dictString] = gridSquare;
        }
        gridSquare.datums.push(d);
    });

    return Object.values(gridSquaresDict);
}
